import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MicrophoneIcon, StopIcon, XMarkIcon, CameraIcon, ArrowLeftIcon, EyeIcon, ChatBubbleLeftRightIcon, CommandLineIcon } from '@heroicons/react/24/outline';
import { getApiUrl, getAuthToken } from '../../services/endpoints';
import { uploadBlob } from '../../services/upload';
import { coworkService } from '../../services/cowork';
import { dashboardService } from '../../services/dashboard';
import { getTransport } from '../../transport';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setCurrentSessionId } from '../../store/slices/coworkSlice';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface DetectionResult {
  label: string;
  confidence: number;
  timestamp: number;
  bbox?: { x1: number; y1: number; x2: number; y2: number; width: number; height: number; confidence: number };
  keypoints?: Array<{ x: number; y: number; confidence: number; index: number }>;
  latencyMs?: number;
  behavior?: { label: string; confidence: number };
  orientation?: { angle: number; circling: boolean };
  trackId?: number;
  headCenter?: { x: number; y: number };
  tailBase?: { x: number; y: number };
}

interface DetectionLogEntry {
  timestamp: number;
  label: string;
  behavior: string;
  confidence: number;
  angle: number;
  headCenter?: { x: number; y: number };
  tailBase?: { x: number; y: number };
  raw: any;
}

const MobileDashboard: React.FC<{ onNavigateHome: () => void }> = ({ onNavigateHome }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isStoppingForSendRef = useRef(false);
  const sessionRef = useRef<string | null>(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAgentResponding, setIsAgentResponding] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraError, setCameraError] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [latestResponse, setLatestResponse] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [showDetectionPanel, setShowDetectionPanel] = useState(true);
  const [detections, setDetections] = useState<DetectionResult[]>([]);
  const [detectionLog, setDetectionLog] = useState<DetectionLogEntry[]>([]);
  const [showJsonLog, setShowJsonLog] = useState(false);
  const detectionCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastDogStateRef = useRef<string | null>(null);
  const dispatch = useDispatch();
  const sessions = useSelector((state: RootState) => state.cowork.sessions);
  const { isSessionActive, isConnected } = useSelector((state: RootState) => state.dashboard);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // Session controls
  const handleStartSession = useCallback(async () => {
    setIsSessionLoading(true);
    try {
      await dashboardService.startSession();
      console.log('[MobileDashboard] Session started');
    } catch (err) {
      console.error('[MobileDashboard] Failed to start session:', err);
    } finally {
      setIsSessionLoading(false);
    }
  }, []);

  const handleStopSession = useCallback(async () => {
    setIsSessionLoading(true);
    try {
      await dashboardService.stopSession();
      lastDogStateRef.current = null;
      console.log('[MobileDashboard] Session stopped');
    } catch (err) {
      console.error('[MobileDashboard] Failed to stop session:', err);
    } finally {
      setIsSessionLoading(false);
    }
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err: any) {
      console.error('[MobileDashboard] Camera error:', err);
      setCameraError(err.message || 'Failed to access camera');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Reset state-change tracking when session stops
  useEffect(() => {
    if (!isSessionActive) {
      lastDogStateRef.current = null;
    }
  }, [isSessionActive]);

  // Ensure dashboard service is connected
  useEffect(() => {
    dashboardService.init();
    dashboardService.connect().catch((err) => {
      console.error('[MobileDashboard] Auto-connect failed:', err);
    });
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [startCamera, stopCamera]);

  // ===== DETECTION: listen for WS 'detection:result' events =====
  useEffect(() => {
    const transport = getTransport();
    if (!transport) return;

    const cleanup = transport.onEvent('detection:result', (payload: any) => {
      if (!payload || !payload.detections) return;
      const newDetections: DetectionResult[] = payload.detections.map((det: any) => ({
        label: det.class || 'unknown',
        confidence: det.confidence || 0,
        timestamp: payload.timestamp || Date.now(),
        bbox: det.bbox,
        keypoints: det.keypoints,
        latencyMs: payload.latencyMs,
        behavior: det.behavior ? { label: det.behavior.label, confidence: det.behavior.confidence } : undefined,
        orientation: det.orientation ? { angle: det.orientation.angle, circling: det.orientation.circling } : undefined,
        trackId: det.trackId,
        headCenter: det.headCenter ? { x: det.headCenter.x, y: det.headCenter.y } : undefined,
        tailBase: det.tailBase ? { x: det.tailBase.x, y: det.tailBase.y } : undefined,
      }));
      setDetections(newDetections);

      // JSON detection logging
      const primary = newDetections[0];
      if (primary) {
        const logEntry: DetectionLogEntry = {
          timestamp: primary.timestamp,
          label: primary.label,
          behavior: primary.behavior?.label ?? 'none',
          confidence: primary.behavior?.confidence ?? primary.confidence,
          angle: primary.orientation?.angle ?? 0,
          headCenter: primary.headCenter,
          tailBase: primary.tailBase,
          raw: payload,
        };
        setDetectionLog(prev => [logEntry, ...prev].slice(0, 50));
        console.log(`[MobileDashboard] Detection: label=${primary.label} behavior=${primary.behavior?.label ?? 'none'} angle=${primary.orientation?.angle?.toFixed(1) ?? 'N/A'}° confidence=${(primary.behavior?.confidence ?? primary.confidence).toFixed(2)}`);
      }

      // State-change-only insertion: only insert when behavior label changes
      if (isSessionActive && primary?.behavior) {
        const newState = primary.behavior.label;
        if (newState !== lastDogStateRef.current) {
          const prevState = lastDogStateRef.current;
          lastDogStateRef.current = newState;
          dashboardService.insertDogPose({
            imageId: `frame_${primary.timestamp}`,
            startedAt: primary.timestamp,
            dogState: newState,
            bboxX: primary.bbox?.x1 ?? 0,
            bboxY: primary.bbox?.y1 ?? 0,
            bboxWidth: primary.bbox?.width ?? 0,
            bboxHeight: primary.bbox?.height ?? 0,
            bboxConfidence: primary.confidence,
            headCenterX: primary.headCenter?.x,
            headCenterY: primary.headCenter?.y,
            tailBaseX: primary.tailBase?.x,
            tailBaseY: primary.tailBase?.y,
            facingDirection: '',
            facingAngle: primary.orientation?.angle ?? 0,
            facingConfidence: primary.behavior?.confidence ?? 0,
            keypoints: primary.keypoints?.map(kp => ({
              name: `kpt_${kp.index}`,
              x: kp.x, y: kp.y, confidence: kp.confidence,
            })) ?? [],
            timestamp: primary.timestamp,
          }).then(success => {
            console.log(`[MobileDashboard] ✅ insertDogPose ${success ? 'SUCCESS' : 'FAILED'}: state=${newState} angle=${primary.orientation?.angle?.toFixed(1) ?? 'N/A'}°`);
          }).catch(err => {
            console.error('[MobileDashboard] ❌ insertDogPose error:', err);
          });
          console.log(`[MobileDashboard] State change: ${prevState ?? 'null'} → ${newState}`);
        }
      }
    });

    return () => cleanup();
  }, [isSessionActive]);

  // ===== DETECTION: capture frames + send to model server via WS (5 FPS) =====
  useEffect(() => {
    if (!cameraActive) {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
      return;
    }

    const transport = getTransport();
    if (!transport) return;

    if (!captureCanvasRef.current) {
      captureCanvasRef.current = document.createElement('canvas');
    }
    const canvas = captureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    frameIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      const targetWidth = 640;
      const scale = targetWidth / video.videoWidth;
      const targetHeight = Math.round(video.videoHeight * scale);
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);

      (transport as any).sendCameraFrame?.(dataUrl, 'mobile').catch(() => {});
    }, 200);

    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, [cameraActive]);

  // ===== DETECTION: draw overlay on canvas =====
  useEffect(() => {
    if (!showDetectionPanel || detections.length === 0) return;
    const canvas = detectionCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const drawOverlay = () => {
      canvas.width = video.clientWidth;
      canvas.height = video.clientHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scaleX = canvas.width / 640;
      const captureHeight = Math.round(640 * (video.videoHeight / video.videoWidth));
      const scaleY = canvas.height / captureHeight;

      for (const det of detections) {
        if (!det.bbox) continue;
        const { x1, y1, x2, y2 } = det.bbox;
        ctx.strokeStyle = det.confidence > 0.7 ? '#22c55e' : '#f59e0b';
        ctx.lineWidth = 2;
        ctx.strokeRect(x1 * scaleX, y1 * scaleY, (x2 - x1) * scaleX, (y2 - y1) * scaleY);

        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        const label = `${det.label} ${(det.confidence * 100).toFixed(0)}%`;
        ctx.font = '12px sans-serif';
        const textWidth = ctx.measureText(label).width;
        ctx.fillRect(x1 * scaleX, y1 * scaleY - 16, textWidth + 8, 16);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x1 * scaleX + 4, y1 * scaleY - 4);

        if (det.keypoints) {
          for (const kp of det.keypoints) {
            if (kp.confidence < 0.3) continue;
            ctx.beginPath();
            ctx.arc(kp.x * scaleX, kp.y * scaleY, 3, 0, 2 * Math.PI);
            ctx.fillStyle = '#3b82f6';
            ctx.fill();
          }
        }
      }
    };

    drawOverlay();
  }, [showDetectionPanel, detections]);

  // STT transcribe
  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string | null> => {
    setIsTranscribing(true);
    try {
      const pseudoPath = `inline:recording_${Date.now()}.webm`;
      const uploaded = await uploadBlob(audioBlob, pseudoPath, 'audio/webm');
      const apiUrl = getApiUrl();
      const token = getAuthToken();
      const response = await fetch(`${apiUrl}/api/stt/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ file: uploaded.filePath, autoDetect: true }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`STT failed (${response.status}): ${errText}`);
      }
      const result = await response.json();
      return (result.text || '').trim() || null;
    } catch (err) {
      console.error('[MobileDashboard] STT failed:', err);
      return null;
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  // Send prompt to agent
  const sendToAgent = useCallback(async (prompt: string) => {
    setIsAgentResponding(true);
    const userMsgId = `mobile-user-${Date.now()}`;
    setChatHistory(prev => [...prev, { id: userMsgId, role: 'user', content: prompt }]);

    const assistantMsgId = `mobile-assistant-${Date.now()}`;
    let accumulatedContent = '';

    let cleanups: Array<(() => void) | undefined> = [];

    try {
      let sessionId = sessionRef.current;
      if (!sessionId) {
        const existingSession = sessions.find(s => s.flowId === 'general-assistant' || s.title === 'Mobile Assistant');
        if (existingSession) {
          sessionId = existingSession.id;
        } else {
          const session = await coworkService.startSession({
            prompt,
            systemPrompt: 'You are a helpful assistant. Respond concisely.',
            title: 'Mobile Assistant',
          });
          if (session) {
            sessionId = session.id;
            dispatch(setCurrentSessionId(session.id));
          }
        }
        sessionRef.current = sessionId;
      }

      if (!sessionId) {
        throw new Error('Failed to create or find session');
      }

      const handleStreamMessage = (data: { sessionId: string; message: any }) => {
        if (data.sessionId !== sessionId) return;
        const msg = data.message;
        if (msg.type === 'assistant' && !msg.metadata?.isThinking && !msg.metadata?.isStreaming) {
          // Final message from server
          accumulatedContent = msg.content || '';
          setChatHistory(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: accumulatedContent }]);
          setLatestResponse(accumulatedContent);
        }
      };

      const handleMessageUpdate = (data: { sessionId: string; messageId: string; content: string }) => {
        if (data.sessionId !== sessionId) return;
        accumulatedContent = data.content;
      };

      const handleComplete = () => {
        // If we didn't get a final message event, use the last accumulated content
        if (accumulatedContent) {
          setChatHistory(prev => {
            if (prev.find(m => m.id === assistantMsgId)) return prev;
            return [...prev, { id: assistantMsgId, role: 'assistant', content: accumulatedContent }];
          });
          setLatestResponse(accumulatedContent);
        }
        setIsAgentResponding(false);
        cleanups.forEach(c => c?.());
        cleanups = [];
      };

      const transport = (await import('../../transport')).getTransport();

      cleanups.push(transport.on('message', handleStreamMessage));
      cleanups.push(transport.on('messageUpdate', handleMessageUpdate));
      cleanups.push(transport.on('complete', handleComplete));

      const isFirstMessage = !sessions.find(s => s.id === sessionId);
      if (!isFirstMessage) {
        await coworkService.continueSession({
          sessionId,
          prompt,
          systemPrompt: 'You are a helpful assistant. Respond concisely.',
        });
      }

      // 2 min timeout fallback
      setTimeout(() => {
        setIsAgentResponding(false);
        cleanups.forEach(c => c?.());
        cleanups = [];
      }, 120000);

    } catch (err: any) {
      console.error('[MobileDashboard] Agent send failed:', err);
      setChatHistory(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: `Error: ${err.message}` }]);
      setLatestResponse(`Error: ${err.message}`);
      setIsAgentResponding(false);
    }
  }, [sessions, dispatch]);

  // Recording handlers
  const handleRecordingToggle = useCallback(async () => {
    if (isRecording) {
      // Stop recording and send
      isStoppingForSendRef.current = true;
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
          mediaRecorderRef.current.stop();
        } else if (mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.resume();
          mediaRecorderRef.current.requestData();
          mediaRecorderRef.current.stop();
        }
      }
    } else if (!isTranscribing && !isAgentResponding) {
      isStoppingForSendRef.current = false;
      audioChunksRef.current = [];
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunksRef.current.push(event.data);
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          stream.getTracks().forEach(track => track.stop());

          // Double-check: cancel may have been clicked during stop() transition
          if (!isStoppingForSendRef.current) return;
          isStoppingForSendRef.current = false;

          const text = await transcribeAudio(audioBlob);
          if (text) {
            await sendToAgent(text);
          }
        };

        mediaRecorder.start(100);
        setIsRecording(true);
        setRecordingTime(0);
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } catch (err) {
        console.error('[MobileDashboard] Recording error:', err);
        setIsRecording(false);
      }
    }
  }, [isRecording, isTranscribing, isAgentResponding, transcribeAudio, sendToAgent]);

  const handleCancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      isStoppingForSendRef.current = false;
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
      if (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.stop();
      }
    }
  }, [isRecording]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="relative h-full w-full overflow-hidden bg-black flex flex-col">
      {/* Camera view — fills the space above the detection panel */}
      <div className="relative flex-1 min-h-0">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          playsInline
          muted
        />
        {/* Detection overlay canvas — renders bbox + keypoints on top of video */}
        {showDetectionPanel && cameraActive && (
          <canvas ref={detectionCanvasRef} className="absolute inset-0 h-full w-full pointer-events-none" />
        )}

        {/* Camera error */}
        {cameraError && !cameraActive && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
            <CameraIcon className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm text-center opacity-80">{cameraError}</p>
            <button
              onClick={startCamera}
              className="mt-4 px-4 py-2 bg-white/20 rounded-xl text-sm text-white backdrop-blur-sm hover:bg-white/30 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => { stopCamera(); onNavigateHome(); }}
          className="absolute top-3 left-3 z-30 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
          aria-label="Back"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>

        {/* Title + Session indicator */}
        <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
          {isSessionActive ? (
            <button
              onClick={handleStopSession}
              disabled={isSessionLoading}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/80 backdrop-blur-sm text-white text-xs font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
              aria-label="Stop session"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Stop
            </button>
          ) : (
            <button
              onClick={handleStartSession}
              disabled={isSessionLoading || !isConnected}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/80 backdrop-blur-sm text-white text-xs font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
              aria-label="Start session"
            >
              ● Rec
            </button>
          )}
          <span className="px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-medium">
            Mobile
          </span>
        </div>

        {/* Toggle detection panel */}
        <button
          onClick={() => setShowDetectionPanel(!showDetectionPanel)}
          className="absolute top-3 right-32 z-30 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
          aria-label="Toggle detection panel"
        >
          <EyeIcon className="h-5 w-5" />
        </button>

        {/* Toggle JSON log */}
        <button
          onClick={() => setShowJsonLog(!showJsonLog)}
          className={`absolute top-3 right-52 z-30 p-2 rounded-full backdrop-blur-sm text-white transition-colors ${showJsonLog ? 'bg-purple-600/80' : 'bg-black/40 hover:bg-black/60'}`}
          aria-label="Toggle JSON log"
        >
          <CommandLineIcon className="h-5 w-5" />
        </button>

        {/* Chat history button */}
        {chatHistory.length > 0 && (
          <button
            onClick={() => setShowChatModal(true)}
            className="absolute top-3 right-52 z-30 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
            aria-label="Chat history"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
          </button>
        )}

        {/* Latest response banner — non-blocking, dismissible */}
        {latestResponse && !isAgentResponding && (
          <div className="absolute bottom-16 left-3 right-3 z-20">
            <div className="rounded-xl bg-black/70 backdrop-blur-md text-white px-3 py-2 text-sm max-h-24 overflow-y-auto">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 whitespace-pre-wrap break-words">
                  {latestResponse}
                </div>
                <button
                  onClick={() => setLatestResponse('')}
                  className="shrink-0 p-0.5 rounded-full hover:bg-white/20 transition-colors"
                  aria-label="Dismiss"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recording status badge */}
        {(isRecording || isTranscribing || isAgentResponding) && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 text-white text-xs flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
            {isRecording && (
              <>
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {formatTime(recordingTime)}
              </>
            )}
            {isTranscribing && (
              <>
                <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Transcribing...
              </>
            )}
            {isAgentResponding && !isTranscribing && (
              <>
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                AI responding...
              </>
            )}
          </div>
        )}

        {/* Floating mic button */}
        <button
          onClick={handleRecordingToggle}
          disabled={isTranscribing || isAgentResponding || !cameraActive}
          className={`absolute bottom-4 right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-white/90 hover:bg-white text-black'
          }`}
          aria-label={isRecording ? 'Stop and send' : 'Start recording'}
        >
          {isRecording ? <StopIcon className="h-5 w-5" /> : <MicrophoneIcon className="h-6 w-6" />}
        </button>

        {/* Cancel recording button */}
        {isRecording && (
          <button
            onClick={handleCancelRecording}
            className="absolute bottom-4 right-20 z-30 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            aria-label="Cancel"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Detection panel — bottom section */}
      {showDetectionPanel && (
        <div className="shrink-0 max-h-[40%] overflow-y-auto bg-white dark:bg-claude-darkSurface border-t dark:border-claude-darkBorder border-gray-200">
          <div className="px-3 py-2 border-b dark:border-claude-darkBorder border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <EyeIcon className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium dark:text-claude-darkText text-gray-800">Detection Results</span>
            </div>
            <span className="text-xs dark:text-claude-darkTextSecondary text-gray-400">
              {detections.length > 0 ? `${detections.length} detection${detections.length > 1 ? 's' : ''}` : 'Waiting...'}
            </span>
          </div>

          {detections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <CameraIcon className="h-8 w-8 text-gray-300 dark:text-claude-darkTextSecondary/40 mb-2" />
              <p className="text-xs text-gray-400 dark:text-claude-darkTextSecondary text-center">
                No detections yet. Detection model will be connected here.
              </p>
              <p className="text-xs text-gray-300 dark:text-claude-darkTextSecondary/60 text-center mt-1">
                (dog pose, object detection, etc.)
              </p>
            </div>
          ) : (
            <div className="divide-y dark:divide-claude-darkBorder divide-gray-100">
              {detections.slice().reverse().map((det, i) => (
                <div key={i} className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium dark:text-claude-darkText text-gray-800 truncate">
                        {det.label}
                        {det.behavior && (
                          <span className="ml-1.5 text-purple-400">· {det.behavior.label}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-claude-darkTextSecondary">
                        <span>{new Date(det.timestamp).toLocaleTimeString()}</span>
                        {det.orientation && (
                          <span className="text-blue-400">{det.orientation.angle.toFixed(0)}°</span>
                        )}
                        {det.latencyMs != null && (
                          <span>{det.latencyMs.toFixed(0)}ms</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs font-medium shrink-0">
                      <span className={
                        det.confidence >= 0.9 ? 'text-green-500' :
                        det.confidence >= 0.7 ? 'text-yellow-500' :
                        'text-red-500'
                      }>
                        {(det.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  {det.orientation?.circling && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-red-500 font-medium animate-pulse">
                      🔄 Circling detected!
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* JSON detection log panel */}
      {showJsonLog && (
        <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h2 className="text-white text-sm font-medium">Detection Log (JSON)</h2>
            <button
              onClick={() => setShowJsonLog(false)}
              className="p-1.5 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
            {detectionLog.length === 0 ? (
              <p className="text-white/40 text-xs text-center mt-8">No detections logged yet</p>
            ) : (
              detectionLog.map((entry, i) => (
                <div key={i} className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-white/50">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                    <span className="text-purple-400 font-medium">{entry.behavior}</span>
                    <span className="text-blue-400">{entry.angle.toFixed(1)}°</span>
                    <span className={
                      entry.confidence >= 0.9 ? 'text-green-400' :
                      entry.confidence >= 0.7 ? 'text-yellow-400' :
                      'text-red-400'
                    }>
                      {(entry.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  {entry.headCenter && (
                    <div className="text-[10px] text-white/30 mt-0.5">
                      head: ({entry.headCenter.x.toFixed(1)}, {entry.headCenter.y.toFixed(1)})
                      {entry.tailBase && ` tail: (${entry.tailBase.x.toFixed(1)}, ${entry.tailBase.y.toFixed(1)})`}
                    </div>
                  )}
                  <details className="mt-1">
                    <summary className="text-[10px] text-white/30 cursor-pointer hover:text-white/50">raw JSON</summary>
                    <pre className="text-[9px] text-white/40 mt-0.5 overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                      {JSON.stringify(entry.raw, null, 1)}
                    </pre>
                  </details>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Chat history modal — separate page overlay */}
      {showChatModal && (
        <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h2 className="text-white text-sm font-medium">Chat History</h2>
            <button
              onClick={() => setShowChatModal(false)}
              className="p-1.5 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {chatHistory.length === 0 ? (
              <p className="text-white/40 text-sm text-center mt-8">No messages yet</p>
            ) : (
              chatHistory.map((msg) => (
                <div
                  key={msg.id}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'ml-auto bg-claude-accent/80 text-white'
                      : 'mr-auto bg-white/10 text-white'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDashboard;